/**
 * PERSONFELT, ROLLER OG DELTAKELSER — kontrakten (spesifikasjonen §4.4–4.5, §7.2, trinn 3).
 *
 * ================================================================================================
 * DEN SPEILER RESSURSLAGET MED VILJE.
 *
 * `person_fields` er `resource_types.fields` for mennesker, og `participations.field_values` er
 * `resources.field_values`. To lag som gjør det samme skal gjøre det likt: den som har lest det ene
 * kan lese det andre, og en feil rettet ett sted er lett å finne igjen i det andre.
 *
 * Forskjellen er hvor definisjonen bor. En ressurstype bærer feltlista si som `jsonb` PÅ typen,
 * fordi typen er én rad man leser hel. Personfeltene er festivalens egne og leses som en LISTE i
 * Innstillinger, sorteres, arkiveres hver for seg — så de er rader.
 *
 * ================================================================================================
 * `ParticipationDto` BÆRER `sources[]` OG IKKE `source`.
 *
 * Tabellen har én `source`: den FØRSTE kilden, den som skrev raden. Unionen vet mer — den leser
 * `edition_people`, `volunteer_signups` og `performance_people` direkte — og et menneske som er
 * både stab og frivillig skal vise begge merkene.
 *
 * Så DTO-en er unionens svar og ikke radens. Det er samme grep som `AllocationDto.people`: klienten
 * får spørsmålet besvart, ikke tabellen utlevert.
 *
 * ================================================================================================
 * «TATT UT AV BYRÅET» ER ET TIDSPUNKT PÅ RADEN, IKKE EN STATUS-ENUM.
 *
 * Fristelsen er `status ∈ (active, withdrawn)`. Et tidspunkt sier NÅR, som er det skjermen trenger
 * for å si «tatt ut 12. juli» og for at noen skal kunne vurdere om skiftene rakk å bli tildelt. En
 * enum ville kastet det, og en enum med to verdier er uansett et boolsk felt med flere bokstaver.
 */
import { z } from 'zod';

import { cursorQuerySchema, page } from '../http/pagination.js';

import { resolvedAccreditationLevelSchema } from './accreditations.js';
import { categoryListParamSchema, participationCategorySchema } from './participation-category.js';
import { personSystemKeySchema } from './person-standard-fields.js';
import { rowViewerSchema } from './row-viewer.js';

// ----------------------------------------------------------------------------- felles vokabular

/** Nøkkelen et felt adresseres på. Samme form som ressursfeltenes — slug, ikke fritekst. */
export const personFieldKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(63)
  .regex(/^[a-z0-9][a-z0-9_-]*$/);

export const PERSON_FIELD_TYPES = ['text', 'number', 'boolean', 'date', 'select'] as const;
export const personFieldTypeSchema = z.enum(PERSON_FIELD_TYPES);
export type PersonFieldType = (typeof PERSON_FIELD_TYPES)[number];

/**
 * Hvor feltet vises.
 *
 *   `panel`          persontidslinjens Detaljer-fane (§7.3)
 *   `volunteer_form` «Bli frivillig»-skjemaet, som da spør om det
 *   `summaries`      «Summert fra personene» — trinn 4s «Liste til kjøkkenet»
 *
 * Ett sett og ikke tre flagg: et fjerde sted i trinn 5 ville vært en fjerde kolonne.
 */
export const PERSON_FIELD_SURFACES = ['panel', 'volunteer_form', 'summaries'] as const;
export const personFieldSurfaceSchema = z.enum(PERSON_FIELD_SURFACES);
export type PersonFieldSurface = (typeof PERSON_FIELD_SURFACES)[number];

/**
 * En verdi i `field_values`.
 *
 * SKALARER OG INGENTING ANNET — samme grunn som ressursenes: `search_text` i basen flater blobben
 * ut med `jsonb_path_query_array(field_values, '$.*')`, og den ser bare ett nivå.
 */
export const personFieldValueSchema = z.union([z.string().max(500), z.number(), z.boolean()]);
export const personFieldValuesSchema = z.record(personFieldKeySchema, personFieldValueSchema);
export type PersonFieldValues = z.infer<typeof personFieldValuesSchema>;

/** Hvor mange felt én festival kan ha. Tjue er «dette er et skjema», som ressurstypens. */
export const MAX_PERSON_FIELDS = 20;

// --------------------------------------------------------------------------------- personfelt

export const personFieldDtoSchema = z
  .object({
    id: z.uuid(),
    /** Null = organisasjonens, delt av alle festivaler. Som ressurstypens. */
    festivalId: z.uuid().nullable(),
    key: personFieldKeySchema,
    label: z.string(),
    type: personFieldTypeSchema,
    options: z.array(z.string()),
    shownIn: z.array(personFieldSurfaceSchema),
    /**
     * Feltet leses fra advancen for et menneske som kom derfra (oppgave 3.4).
     *
     * IKKE EN KOPI VED OVERFØRING — et oppslag ved lesing, så byråets rettelse en uke senere er
     * den som vises. Hvilken kilde `key` leser fra avgjøres i API-et; se `ADVANCE_FIELDS`.
     */
    readFromAdvance: z.boolean(),
    sortOrder: z.number().int(),
    archivedAt: z.iso.datetime().nullable(),
    /**
     * STANDARDFELTET feltet er (0920), eller null for organisasjonens egne felt.
     *
     * Et standardfelt vises med hengelås, kan ikke slettes eller gis nytt navn, og verdien leses fra
     * personen (`app.person_card`) — aldri fra `participations.field_values`.
     */
    systemKey: personSystemKeySchema.nullable(),
  })
  .meta({ id: 'PersonField' });
export type PersonFieldDto = z.infer<typeof personFieldDtoSchema>;

/** `select` uten valg er et nedtrekk med ingenting i — den samme regelen ressursfeltet har. */
const selectHasOptions = (value: { type: PersonFieldType; options?: readonly string[] }): boolean =>
  value.type !== 'select' || (value.options?.length ?? 0) > 0;

export const createPersonFieldRequestSchema = z
  .object({
    festivalId: z.uuid().nullable(),
    label: z.string().trim().min(1).max(60),
    type: personFieldTypeSchema,
    options: z.array(z.string().trim().min(1).max(60)).max(50).default([]),
    shownIn: z.array(personFieldSurfaceSchema).min(1).default(['panel']),
    readFromAdvance: z.boolean().default(false),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine(selectHasOptions, {
    message: 'A select field must offer at least one option.',
    path: ['options'],
  })
  .meta({ id: 'CreatePersonFieldRequest' });
export type CreatePersonFieldRequest = z.infer<typeof createPersonFieldRequestSchema>;

/**
 * `key` og `festivalId` er fraværende, som på ressurstypen og av samme to grunner: slugen
 * genereres av det første navnet og adresserer feltet i `field_values` for alltid, og å flytte et
 * felt mellom festival og organisasjon er å flytte hver verdi det bærer.
 *
 * `type` er OGSÅ fraværende, og det er en tredje grunn: en `select` som blir en `number` gjør hver
 * lagrede verdi ugyldig i stillhet. Skal typen endres, er svaret et nytt felt.
 */
export const updatePersonFieldRequestSchema = z
  .object({
    label: z.string().trim().min(1).max(60).optional(),
    options: z.array(z.string().trim().min(1).max(60)).max(50).optional(),
    shownIn: z.array(personFieldSurfaceSchema).min(1).optional(),
    readFromAdvance: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    /** `true` arkiverer, `false` henter tilbake. Verdiene blir stående uansett. */
    archived: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdatePersonFieldRequest' });
export type UpdatePersonFieldRequest = z.infer<typeof updatePersonFieldRequestSchema>;

export const listPersonFieldsQuerySchema = z
  .object({
    festivalId: z.uuid().optional(),
    includeArchived: z.stringbool().default(false),
  })
  .meta({ id: 'ListPersonFieldsQuery' });
export type ListPersonFieldsQuery = z.infer<typeof listPersonFieldsQuerySchema>;

export const personFieldListResponseSchema = z
  .object({ items: z.array(personFieldDtoSchema) })
  .meta({ id: 'PersonFieldListResponse' });
export type PersonFieldListResponse = z.infer<typeof personFieldListResponseSchema>;

/**
 * HVA EN SLETTING TAR MED SEG (A5). `GET /v1/person-fields/:id/usage` teller svarene som slettes:
 * verdiene på deltakelsene og svarene i påmeldingsskjemaene som spør med feltet. Bekreftelsen sier
 * tallet før noen trykker.
 */
export const personFieldUsageDtoSchema = z
  .object({ answers: z.number().int().min(0) })
  .meta({ id: 'PersonFieldUsage' });
export type PersonFieldUsageDto = z.infer<typeof personFieldUsageDtoSchema>;

// -------------------------------------------------------------------------------------- roller

export const participationRoleDtoSchema = z
  .object({
    id: z.uuid(),
    key: z.string(),
    name: z.string(),
    colour: z.string().nullable(),
    sortOrder: z.number().int(),
    archivedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'ParticipationRole' });
export type ParticipationRoleDto = z.infer<typeof participationRoleDtoSchema>;

export const createParticipationRoleRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(60),
    colour: z.string().trim().min(1).max(30).nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .meta({ id: 'CreateParticipationRoleRequest' });
export type CreateParticipationRoleRequest = z.infer<typeof createParticipationRoleRequestSchema>;

export const updateParticipationRoleRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(60).optional(),
    colour: z.string().trim().min(1).max(30).nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    archived: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateParticipationRoleRequest' });
export type UpdateParticipationRoleRequest = z.infer<typeof updateParticipationRoleRequestSchema>;

export const listParticipationRolesQuerySchema = z
  .object({ includeArchived: z.stringbool().default(false) })
  .meta({ id: 'ListParticipationRolesQuery' });
export type ListParticipationRolesQuery = z.infer<typeof listParticipationRolesQuerySchema>;

export const participationRoleListResponseSchema = z
  .object({ items: z.array(participationRoleDtoSchema) })
  .meta({ id: 'ParticipationRoleListResponse' });
export type ParticipationRoleListResponse = z.infer<typeof participationRoleListResponseSchema>;

// -------------------------------------------------------------------------------- deltakelser

/**
 * Hvorfor mennesket er her.
 *
 *   `contacts`        skrevet inn som stab i årgangen
 *   `volunteer_form`  meldte seg selv, og noen godkjente
 *   `advance`         byråets folk, fra et reisefølge
 *   `manual`          noen la dem til her, uten en kilde bak
 *
 * `crew` er bevisst IKKE en av dem: kilden er registeret de kom fra, ikke hva de gjør. Hva de gjør
 * er rollen, og den er en egen liste festivalen eier.
 */
/**
 * HVOR ET MENNESKE KOM FRA — og `member` er den femte (QA13 runde 2, #28 / J1).
 *
 * ------------------------------------------------------------------------------------------------
 * Atle: «Organisasjonens medlemmer (Atle) kan ikke velges som ansvarlig på en ressurs.»
 *
 * Målt i basen: han er medlem av God Nok, han er kontakt i God Nok, og han har NULL rader i
 * `edition_people`, `participations`, `volunteer_signups` og `performance_people`. Unionen bak
 * `GET /editions/:id/people` hadde fire armer, og han var ikke i noen av dem — så hver eneste
 * personvelger som leser den lista, tilbød alle andre enn ham.
 *
 * `member` er ikke en rad noen skriver. Den er en kilde på lik linje med de fire andre: «hen jobber
 * her». Ingen migrering, fordi ingenting lagres — `participations.source` tar fortsatt bare de fire
 * som faktisk kan stå i en rad.
 */
export const PARTICIPATION_SOURCES = [
  'contacts',
  'volunteer_form',
  'advance',
  'manual',
  'member',
  /* 1096: leverandøren og de ansatte (persontypene §3.2). */
  'supplier',
] as const;
export const participationSourceSchema = z.enum(PARTICIPATION_SOURCES);
export type ParticipationSource = (typeof PARTICIPATION_SOURCES)[number];

/** En rolle slik en rad tegner den: navnet og fargen, ikke hele definisjonen. */
export const participationRoleRefSchema = z
  .object({ id: z.uuid(), name: z.string(), colour: z.string().nullable() })
  .meta({ id: 'ParticipationRoleRef' });

export const participationDtoSchema = z
  .object({
    /**
     * Deltakelsens id. ALLTID SATT I LISTA siden B1 (0916/0917): lista leser bare `participations`,
     * og hver rad har derfor boks og meny (punkt 41).
     *
     * Null bare i oppslaget på ÉN kontakt (`contactId`) for en frivillig som venter og derfor ikke
     * er med i årgangen ennå — panelet viser påmeldingen hens (QA 22. september, punkt 3).
     */
    id: z.uuid().nullable(),
    festivalEditionId: z.uuid(),
    contactId: z.uuid(),
    personId: z.uuid(),
    displayName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),

    accreditationLevelId: z.uuid().nullable(),
    accreditationLevelName: z.string().nullable(),
    /**
     * NIVÅET PÅ ARMBÅNDET (215.21), og det panelet viser. Feltene over er deltakelsens eget valg og
     * det den skriver; dette er hva akkrediteringen faktisk gir, og hvor det kom fra.
     */
    resolvedLevel: resolvedAccreditationLevelSchema,
    days: z.array(z.iso.date()),
    fieldValues: personFieldValuesSchema,

    /**
     * Kildene som merkelapper på raden (`participation_sources`), i det gamle ordforrådet:
     * stab/avdeling → `contacts`, frivillig → `volunteer_form`, reisefølge, ledelse og arrangørens
     * folk → `advance`, medlem → `member`, for hånd → `manual`.
     */
    sources: z.array(participationSourceSchema),
    /** Stab, frivillig, artist, gjest, presse (B1). Kan endres. Erstatter «Kilde» og «Type». */
    category: participationCategorySchema,
    /** Hva personen gjør, som fritekst. Lineup-rollen og stabens tittel. Aldri vaktposten. */
    roleTitle: z.string().nullable(),
    /** Passtypen på raden (B1). */
    accreditationPassId: z.uuid().nullable(),
    accreditationPassName: z.string().nullable(),
    /** Actene personen er med (reisefølge, ledelse, arrangørens folk) — «Reiser med Ari Bajgora». */
    acts: z.array(z.string()),
    /** Byrået eier navn, telefon og e-post. Skriving svares med 409 (Atles avgjørelse 4). */
    identityLocked: z.boolean(),
    /** Huset som eier identiteten, når den er låst — «Standing Ovation · advance». */
    lockedByName: z.string().nullable(),
    /**
     * FORNAVN, ETTERNAVN OG INNLOGGING (260.28, 257.12, 0964). Navnet er personens; den med innlogging
     * fører det selv i profilen, så skuffen viser det som tekst. Valgfrie for testoppsett.
     */
    givenName: z.string().nullable().optional(),
    familyName: z.string().nullable().optional(),
    hasLogin: z.boolean().optional(),
    /** Satt når byrået har tatt dem ut. Raden står; se migrering 0205. */
    withdrawnAt: z.iso.datetime().nullable(),

    roles: z.array(participationRoleRefSchema),
    departmentIds: z.array(z.uuid()),
    /** De av `departmentIds` personen LEDER (sak 211.2): lista sier «Markedsføring (leder)». */
    leadDepartmentIds: z.array(z.uuid()),
    /**
     * ØNSKET avdeling fra frivilligskjemaet, når de ikke er plassert ennå.
     *
     * §7.2: «Uten avdeling» er innboksen for nye frivillige: «viser ønsket avdeling fra skjemaet og
     * ‹Plasser›». Et ønske er ikke en plassering, og det er derfor det er et eget felt framfor en
     * rad i `participation_departments` — backfillen sier det samme.
     */
    wantedDepartmentName: z.string().nullable(),
    /**
     * PÅMELDINGEN, NÅR MENNESKET HAR EN (QA13 runde 2, #20).
     *
     * ------------------------------------------------------------------------------------------
     * Atle: «To ulike personpaneler — skal være ett … Én komponent, uansett inngang, der seksjoner
     * som ikke gjelder, skjules.»
     *
     * Det rike panelet levde på `volunteer_signups.id`, det andre på deltakelsen, og de to id-ene
     * fantes ikke på samme sted. Et panel som skulle vise begge halvdeler kunne derfor ikke finne
     * den andre. Denne er broen: er den satt, henter panelet behandlingen, skjemasvarene, tidligere
     * årganger og notatene. Er den null, finnes de seksjonene ikke å vise.
     */
    volunteerSignupId: z.uuid().nullable(),
    /** Hva leseren kan gjøre med raden (D1), regnet av `can()` på serveren. */
    viewer: rowViewerSchema.optional(),
  })
  .meta({ id: 'Participation' });
export type ParticipationDto = z.infer<typeof participationDtoSchema>;

export const PARTICIPATION_SORT_KEYS = ['name'] as const;

export const listParticipationsQuerySchema = cursorQuerySchema(PARTICIPATION_SORT_KEYS)
  .extend({
    editionId: z.uuid(),
    /** Avdelingen i treet. Tar med seksjonene under, som Ressurser. `none` er «Uten avdeling». */
    departmentId: z.union([z.uuid(), z.literal('none')]).optional(),
    participationRoleId: z.uuid().optional(),
    /**
     * ÉN PERSON, SLÅTT OPP PÅ KONTAKTEN (QA13 runde 2, #20).
     *
     * Frivillige-fana kjenner mennesket som en `volunteer_signups`-rad og har `contactId` på den.
     * Det samlede personpanelet vil ha deltakelsen. Uten dette filteret måtte fana enten hente hele
     * lista og lete, eller bygge en halv DTO selv — og en halv DTO ville vist feil roller og feil
     * avdelinger til den første oppfriskningen.
     *
     * Et filter og ikke en egen rute: det er den samme raden, den samme unionen og den samme
     * cellen. En `GET /participations/by-contact/:id` ville vært et andre sted å holde de nitten
     * kolonnene i takt.
     */
    contactId: z.uuid().optional(),
    source: participationSourceSchema.optional(),
    category: participationCategorySchema.optional(),
    /**
     * Fritekst mot radens ene søketekst: navn, telefon, e-post, rolle, avdeling, act og
     * personfeltene (punkt 53). Kategorien treffes gjennom `qCategories`.
     */
    q: z.string().trim().min(1).max(120).optional(),
    /** Kategoriene søketeksten treffer på brukerens språk — se `categoryListParamSchema`. */
    qCategories: categoryListParamSchema.optional(),
    /** Tatt ut av byrået vises som standard — det er en mangel noen skal rydde, ikke støy. */
    includeWithdrawn: z.stringbool().default(true),
  })
  .meta({ id: 'ListParticipationsQuery' });
export type ListParticipationsQuery = z.infer<typeof listParticipationsQuerySchema>;

export const participationListResponseSchema = page(
  participationDtoSchema,
  'ParticipationListResponse',
);
export type ParticipationListResponse = z.infer<typeof participationListResponseSchema>;

/**
 * Å legge til én person (§7.6): «navn og det viktigste først, ett steg».
 *
 * `contactId` ELLER `displayName`, aldri begge: enten peker man på noen registeret kjenner, eller
 * så skriver man et navn og registeret får en ny. Det er den samme todelingen «Ny person» på
 * programpostens People-fane har, og den er der fordi et skjema som krevde e-post ville stoppet
 * den halvparten av en frivilligstab som ikke har oppgitt en.
 */
export const createParticipationRequestSchema = z
  .object({
    editionId: z.uuid(),
    contactId: z.uuid().optional(),
    displayName: z.string().trim().min(1).max(160).optional(),
    /** 260.28: navnedelene, lagret på personen. `displayName` er dem satt sammen. */
    givenName: z.string().trim().max(100).nullish(),
    familyName: z.string().trim().max(100).nullish(),
    email: z.email().max(255).nullish(),
    phone: z.string().trim().min(1).max(40).nullish(),
    accreditationLevelId: z.uuid().nullish(),
    /** Passtypen (QA13 #66, 0916): på raden, så «Legg til person» kan sette den med en gang. */
    accreditationPassId: z.uuid().nullish(),
    departmentIds: z.array(z.uuid()).max(20).default([]),
    participationRoleIds: z.array(z.uuid()).max(20).default([]),
    fieldValues: personFieldValuesSchema.default({}),
    days: z.array(z.iso.date()).max(60).default([]),
    /** Stab som standard: den som legges til for hånd her er som regel husets eget folk. */
    category: participationCategorySchema.optional(),
    roleTitle: z.string().trim().max(120).nullish(),
  })
  .refine((value) => (value.contactId === undefined) !== (value.displayName === undefined), {
    message: 'Name a contact or give a display name, not both.',
    path: ['contactId'],
  })
  .meta({ id: 'CreateParticipationRequest' });
export type CreateParticipationRequest = z.infer<typeof createParticipationRequestSchema>;

/**
 * IDENTITETSFELTENE STÅR IKKE HER, og det er avgjørelse 2A som form.
 *
 * Navn, e-post og telefon er kontaktens, ikke deltakelsens — de rettes i Kontakter, og for et
 * menneske byrået eier rettes de ikke i det hele tatt (409). At de mangler i denne kontrakten er
 * derfor ikke en forglemmelse: det er stedet regelen bor når den skal være umulig å bryte.
 *
 * ================================================================================================
 * `addDepartmentIds` OG `removeDepartmentIds` — SAK 180, ATLES SVAR 22. SEPTEMBER.
 *
 * «Personer: "Legg til i avdeling" og "Fjern fra avdeling", ingen "erstatt".»
 *
 * `departmentIds` ERSTATTER hele lista, og det er riktig for ÉN person i et panel der man ser hva
 * hen står i fra før. Det er feil for tolv markerte rader: den som alt står i «Bar» og «Rigg»
 * mister begge, og det finnes ingen angre. Det var hele spørsmålet i sak 180.
 *
 * Så kontrakten kan nå si de to andre setningene. De er MENGDEOPERASJONER og ikke en ny verdi:
 * «legg til Bar» på en som alt står i Bar er et lovlig ingenting, og «fjern Bar» fra en som ikke
 * står der er det samme. Det er nettopp den egenskapen som gjør dem trygge på tolv rader.
 *
 * ERSTATT OG LEGG-TIL/FJERN I SAMME KROPP AVVISES. De to er motstridende beskjeder om det samme
 * settet, og en rekkefølge mellom dem ville vært en usynlig regel — så parseren sier nei i stedet
 * for at tjenesten velger en vinner.
 */
export const updateParticipationRequestSchema = z
  .object({
    accreditationLevelId: z.uuid().nullish(),
    departmentIds: z.array(z.uuid()).max(20).optional(),
    /** Additiv. Se filhodet: en avdeling personen alt står i er et lovlig ingenting. */
    addDepartmentIds: z.array(z.uuid()).max(20).optional(),
    /** Subtraktiv, og like tålmodig: en avdeling personen ikke står i er et lovlig ingenting. */
    removeDepartmentIds: z.array(z.uuid()).max(20).optional(),
    participationRoleIds: z.array(z.uuid()).max(20).optional(),
    fieldValues: personFieldValuesSchema.optional(),
    days: z.array(z.iso.date()).max(60).optional(),
    /** B1: kategorien kan endres (hurtigredigering og skuff). */
    category: participationCategorySchema.optional(),
    /** B1: rollen som fritekst. Tom streng eller null fjerner den. */
    roleTitle: z.string().trim().max(120).nullish(),
    accreditationPassId: z.uuid().nullish(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .superRefine((value, ctx) => {
    const setting = value.departmentIds !== undefined;
    const nudging = value.addDepartmentIds !== undefined || value.removeDepartmentIds !== undefined;
    if (setting && nudging) {
      ctx.addIssue({
        code: 'custom',
        path: ['departmentIds'],
        message: 'Replace the list or nudge it, never both in one request.',
      });
    }
  })
  .meta({ id: 'UpdateParticipationRequest' });
export type UpdateParticipationRequest = z.infer<typeof updateParticipationRequestSchema>;

// ------------------------------------------------------- tellerne i avdelingstreet (§7.2)

/**
 * FASTE TELLERE FOR ÅRGANGEN, IKKE FILTRERTE — avgjørelse 6A, lest på nytt for personer.
 *
 * `unstaffed` er ikke et antall personer: det er antall UBEMANNEDE SKIFT i avdelingen, som er det
 * treet i §7.2 faktisk viser («tellere personer / ubemannet»). De to tallene svarer på hver sin
 * halvdel av «hvordan står det til her», og et tre som bare hadde det første ville sagt at en
 * avdeling med tjue personer og ti tomme skift er i orden.
 */
export const participationDepartmentSummaryRowSchema = z
  .object({
    /** Null er «Uten avdeling» — innboksen treet viser sist. */
    departmentId: z.uuid().nullable(),
    people: z.number().int(),
    unstaffed: z.number().int(),
  })
  .meta({ id: 'ParticipationDepartmentSummaryRow' });

export const participationDepartmentSummarySchema = z
  .object({
    items: z.array(participationDepartmentSummaryRowSchema),
    totalPeople: z.number().int(),
    totalUnstaffed: z.number().int(),
  })
  .meta({ id: 'ParticipationDepartmentSummary' });
export type ParticipationDepartmentSummary = z.infer<typeof participationDepartmentSummarySchema>;

export const participationDepartmentSummaryQuerySchema = z
  .object({ editionId: z.uuid() })
  .meta({ id: 'ParticipationDepartmentSummaryQuery' });
export type ParticipationDepartmentSummaryQuery = z.infer<
  typeof participationDepartmentSummaryQuerySchema
>;
